import React from 'react'
import { InvoiceDetail } from '@/types'
import { Modal } from '@/components/ui/Modal'
import { FilePdf } from 'phosphor-react'
import {
  formatCurrency,
  formatInvoiceDate,
  formatInvoicePeriod,
  getBilledEntityLabel,
  getBilledEntityTypeLabel,
  getInvoiceLineTypeBadgeClasses,
  getInvoiceLineTypeLabel,
} from '@/utils/facturation-utils'

interface InvoiceDetailModalProps {
  invoice: InvoiceDetail | null
  isLoading: boolean
  showEntityInfo?: boolean
  showResellerGroups?: boolean
  onClose: () => void
}

export function InvoiceLinesTable({ lines }: { lines: InvoiceDetail['lines'] }) {
  if (!lines?.length) {
    return (
      <p className="text-sm text-[var(--color-text-secondary)]">Aucune ligne de facturation.</p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="min-w-full text-sm">
        <thead className="bg-[var(--color-gray-50)]">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
              Client
            </th>
            <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
              Trimestre
            </th>
            <th className="px-4 py-3 text-left font-semibold text-[var(--color-text-primary)]">
              Type
            </th>
            <th className="px-4 py-3 text-right font-semibold text-[var(--color-text-primary)]">
              Prix unitaire
            </th>
            <th className="px-4 py-3 text-right font-semibold text-[var(--color-text-primary)]">
              Montant HT
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {lines.map(line => (
            <tr key={`${line.clientId}-${line.quarterLabel}-${line.lineType}`}>
              <td className="px-4 py-3 text-[var(--color-text-primary)]">{line.clientName}</td>
              <td className="px-4 py-3 text-[var(--color-text-secondary)]">{line.quarterLabel}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getInvoiceLineTypeBadgeClasses(line.lineType)}`}
                >
                  {getInvoiceLineTypeLabel(line.lineType)}
                </span>
              </td>
              <td className="px-4 py-3 text-right text-[var(--color-text-primary)]">
                {formatCurrency(line.unitPrice)}
              </td>
              <td className="px-4 py-3 text-right font-medium text-[var(--color-text-primary)]">
                {formatCurrency(line.amountHt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  invoice,
  isLoading,
  showEntityInfo = false,
  showResellerGroups = false,
  onClose,
}) => {
  if (!invoice && !isLoading) return null

  return (
    <Modal
      title="Détail de la facture"
      onCancel={onClose}
      onConfirm={onClose}
      confirmButtonText="Fermer"
      showCancel={false}
      isLoading={isLoading}
      size="lg"
    >
      {isLoading ? (
        <p className="text-[var(--color-text-secondary)]">Chargement du détail...</p>
      ) : invoice ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                Période
              </p>
              <p className="text-[var(--color-text-primary)] font-medium">
                {formatInvoicePeriod(invoice.periodMonth, invoice.periodYear)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                Date d&apos;émission
              </p>
              <p className="text-[var(--color-text-primary)]">
                {formatInvoiceDate(invoice.generatedAt)}
              </p>
            </div>
            {showEntityInfo && invoice.billedEntity && (
              <>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                    Entité facturée
                  </p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {getBilledEntityLabel(invoice.billedEntity)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                    Type d&apos;entité
                  </p>
                  <p className="text-[var(--color-text-primary)]">
                    {getBilledEntityTypeLabel(invoice.billedEntity.type)}
                  </p>
                </div>
                {invoice.billedEntity.fluxId != null && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                      Identifiant Flux
                    </p>
                    <p className="text-[var(--color-text-primary)]">{invoice.billedEntity.fluxId}</p>
                  </div>
                )}
              </>
            )}
            {invoice.fluxInvoiceId && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                  Référence Flux
                </p>
                <p className="text-[var(--color-text-primary)]">{invoice.fluxInvoiceId}</p>
              </div>
            )}
          </div>

          {invoice.pdfUrl && (
            <a
              href={invoice.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-[var(--color-primary)] hover:bg-[var(--color-gray-50)] transition-colors"
            >
              <FilePdf className="w-5 h-5" weight="regular" />
              Télécharger la facture PDF
            </a>
          )}

          {showResellerGroups && invoice.resellerGroups?.length ? (
            <div className="space-y-6">
              {invoice.resellerGroups.map(group => (
                <div key={group.reseller._id} className="space-y-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                      {group.reseller.companyName ||
                        `${group.reseller.firstname ?? ''} ${group.reseller.lastname ?? ''}`.trim() ||
                        group.reseller.email}
                    </h3>
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      {group.lines.length} licence(s) — TTC {formatCurrency(group.totalTtc)}
                    </p>
                  </div>
                  <InvoiceLinesTable lines={group.lines} />
                  <div className="flex justify-end gap-6 text-sm">
                    <span className="text-[var(--color-text-secondary)]">
                      HT : <strong>{formatCurrency(group.totalHt)}</strong>
                    </span>
                    <span className="text-[var(--color-text-secondary)]">
                      TVA : <strong>{formatCurrency(group.totalVat)}</strong>
                    </span>
                    <span className="text-[var(--color-text-primary)]">
                      TTC : <strong>{formatCurrency(group.totalTtc)}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <InvoiceLinesTable lines={invoice.lines} />
          )}

          <div className="rounded-lg bg-[var(--color-gray-50)] border border-[var(--color-border)] p-4">
            <div className="flex flex-wrap justify-end gap-6 text-sm">
              <span className="text-[var(--color-text-secondary)]">
                Tarif unitaire : <strong>{formatCurrency(invoice.unitPrice)}</strong>
              </span>
              <span className="text-[var(--color-text-secondary)]">
                Taux TVA : <strong>{invoice.vatRate} %</strong>
              </span>
              <span className="text-[var(--color-text-secondary)]">
                Total HT : <strong>{formatCurrency(invoice.totalHt)}</strong>
              </span>
              <span className="text-[var(--color-text-secondary)]">
                TVA : <strong>{formatCurrency(invoice.totalVat)}</strong>
              </span>
              <span className="text-[var(--color-text-primary)] font-semibold">
                Total TTC : {formatCurrency(invoice.totalTtc)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
